import React, { useState } from 'react';
import { AlertCircle, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { META_LABEL, FormSection } from '../playbooks/editorBits';
import StructuredValues from '../../structured-data/StructuredValues';
import { testRunSchema } from '../../structured-data/structuredDataApi';
import ContractPicker from './ContractPicker';

const PRE_STYLE = {
  margin: 0,
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  background: 'var(--surface-hover)',
  borderRadius: 'var(--corner-radius-sm)',
  padding: 12,
  maxHeight: 320,
  overflow: 'auto',
};

// Dry run of the current draft (including unsaved edits) against a real
// contract. Nothing is stored and the contract is not changed.
export default function TestRunPane({ draft }) {
  const [picked, setPicked] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const canRun = !!picked && (draft.fields || []).length > 0 && !running;

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await testRunSchema({ contractId: picked.id, schema: draft });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <FormSection
      title="Test against a contract"
      hint="Dry run with the current draft — including unsaved edits. Nothing is stored and the contract is not changed."
    >
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <ContractPicker picked={picked} onPick={setPicked} onClear={() => setPicked(null)} ariaLabel="Search contracts for a test run" />
        <button type="button" className="btn outline sm" onClick={run} disabled={!canRun}>
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {running ? 'Running…' : 'Test run'}
        </button>
      </div>

      {error && (
        <div className="callout red">
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body" style={{ fontSize: 12.5 }}>Test run failed: {error}</div>
        </div>
      )}

      {result && (
        <div className="stack-sm">
          {result.warnings?.length > 0 && (
            <div className="callout yellow">
              <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
              <div className="callout-body" style={{ fontSize: 12 }}>
                <strong>The response didn't fully match the declared shape:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  {result.warnings.map((w, i) => <li key={i} className="mono" style={{ fontSize: 11.5 }}>{w}</li>)}
                </ul>
              </div>
            </div>
          )}
          <div>
            <button
              type="button"
              onClick={() => setPromptOpen(!promptOpen)}
              aria-expanded={promptOpen}
              className="row"
              style={{ gap: 6, background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--text-secondary)' }}
            >
              <ChevronRight className="w-[12px] h-[12px]" style={{ opacity: 0.5, transform: promptOpen ? 'rotate(90deg)' : 'none', transition: 'transform 160ms' }} />
              Rendered prompt
            </button>
            {promptOpen && <pre className="mono" style={{ ...PRE_STYLE, marginTop: 6 }}>{result.prompt}</pre>}
          </div>
          <div>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ ...META_LABEL, marginBottom: 0 }}>Result</div>
              <button
                type="button"
                className="btn ghost xs"
                onClick={() => setShowRaw(!showRaw)}
                aria-pressed={showRaw}
              >
                {showRaw ? 'Typed view' : 'Raw JSON'}
              </button>
            </div>
            {showRaw ? (
              <pre className="mono" style={PRE_STYLE}>{JSON.stringify(result.values, null, 2)}</pre>
            ) : (
              <StructuredValues fields={result.fields || draft.fields || []} values={result.values || {}} />
            )}
          </div>
          <div className="muted mono" style={{ fontSize: 11 }}>
            {result.model}
            {result.inputTokens != null ? ` · ${(result.inputTokens + (result.outputTokens || 0)).toLocaleString()} tokens` : ''}
            {result.durationMs != null ? ` · ${(result.durationMs / 1000).toFixed(1)}s` : ''}
          </div>
        </div>
      )}
    </FormSection>
  );
}
